import React from "react";
import { Box } from "@mui/material";
import type { RankingParam } from "./rankingCardCopy";

/** Six ranking dimensions — primary param per dimension (see `RANKING_TYPES`). */
const DIMENSIONS: { label: string; fill: string; param: RankingParam }[] = [
  { label: "Distribución", fill: "#378ADD", param: "channels-social" },
  { label: "Audiencia", fill: "#7F77DD", param: "views" },
  { label: "Interacción", fill: "#D85A30", param: "interactions" },
  { label: "Comunidad", fill: "#1D9E75", param: "livestream-programs" },
  { label: "Marcas", fill: "#BA7517", param: "programs-brands" },
  { label: "Copa Mundial", fill: "#326295", param: "world-cup-views" },
];

const CIRCLE_PX = 32;

function connectorSx(colorScheme: "default" | "darkCard") {
  return {
    width: 12,
    height: 2,
    borderRadius: 999,
    flexShrink: 0,
    bgcolor: colorScheme === "darkCard" ? "rgba(255,255,255,0.35)" : "grey.300",
  } as const;
}

const circleBase = (isDark: boolean) => ({
  width: CIRCLE_PX,
  height: CIRCLE_PX,
  minWidth: CIRCLE_PX,
  minHeight: CIRCLE_PX,
  p: 0,
  flexShrink: 0,
  boxSizing: "border-box" as const,
  border: "2px solid",
  borderColor: isDark ? "rgba(255,255,255,0.45)" : "grey.400",
  bgcolor: isDark ? "transparent" : "background.paper",
  borderRadius: "50%",
});

export function rankingStepIndexForParam(param: RankingParam): number {
  const normalized: RankingParam =
    param === "livestream-programs-program"
      ? "livestream-programs"
      : param === "world-cup-livestream-programs"
        ? "world-cup-views"
        : param === "channels-views"
          ? "views"
          : param === "interactions-total" || param === "channels-interactions"
            ? "interactions"
            : param;
  const i = DIMENSIONS.findIndex((d) => d.param === normalized);
  return i >= 0 ? i : 0;
}

/** Fill color for the active dimension (e.g. screenshot footer explainer band). */
export function getRankingDimensionFillForParam(param: RankingParam): string {
  const i = rankingStepIndexForParam(param);
  return DIMENSIONS[i]?.fill ?? DIMENSIONS[0].fill;
}

/** Spanish label for the dimension (e.g. aria-label). */
export function getRankingDimensionLabelForParam(param: RankingParam): string {
  const i = rankingStepIndexForParam(param);
  return DIMENSIONS[i]?.label ?? DIMENSIONS[0].label;
}

export interface RankingDimensionStepperProps {
  selectedRankingParam: RankingParam;
  /** Omit on ranking card footers (read-only). */
  onSelectRankingParam?: (param: RankingParam) => void;
  /** `darkCard`: footer on `#1a1a1a` ranking asset. */
  colorScheme?: "default" | "darkCard";
  /** If true, only the active dimension pill is shown (no circles or separators). */
  simple?: boolean;
}

function ActiveDimensionChip({ label, fill }: { label: string; fill: string }) {
  return (
    <Box
      sx={{
        height: CIRCLE_PX,
        px: 1,
        borderRadius: 999,
        bgcolor: fill,
        color: "#fff",
        fontSize: 16,
        fontWeight: 600,
        lineHeight: `${CIRCLE_PX}px`,
        letterSpacing: "0.01em",
        whiteSpace: "nowrap",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
      }}
    >
      {label}
    </Box>
  );
}

/**
 * Six-dimension stepper: active dimension is a filled pill.
 * Full mode: inactive steps are 24px hollow circles with 12×2 connectors (`simple={false}`, default).
 * Simple mode: active pill only (`simple`).
 */
export function RankingDimensionStepper({
  selectedRankingParam,
  onSelectRankingParam,
  colorScheme = "default",
  simple = false,
}: RankingDimensionStepperProps) {
  const activeStep = rankingStepIndexForParam(selectedRankingParam);
  const active = DIMENSIONS[activeStep] ?? DIMENSIONS[0];
  const interactive = onSelectRankingParam != null;
  const isDark = colorScheme === "darkCard";

  if (simple) {
    return (
      <Box
        role="status"
        aria-label={`Dimensión del ranking: ${active.label}`}
        sx={{
          display: "flex",
          alignItems: "center",
          minHeight: CIRCLE_PX,
        }}
      >
        <ActiveDimensionChip label={active.label} fill={active.fill} />
      </Box>
    );
  }

  return (
    <Box
      role="group"
      aria-label="Dimensión del ranking"
      sx={{
        display: "flex",
        alignItems: "center",
        minHeight: CIRCLE_PX,
        flexWrap: "wrap",
        gap: "2px",
      }}
    >
      {DIMENSIONS.map((dim, i) => (
        <React.Fragment key={dim.param}>
          {i > 0 && <Box aria-hidden sx={connectorSx(colorScheme)} />}
          {activeStep === i ? (
            <ActiveDimensionChip label={dim.label} fill={dim.fill} />
          ) : interactive ? (
            <Box
              component="button"
              type="button"
              aria-label={`${dim.label}: usar rankings de esta dimensión`}
              onClick={() => onSelectRankingParam!(dim.param)}
              sx={{
                ...circleBase(isDark),
                cursor: "pointer",
                "&:hover": {
                  borderColor: dim.fill,
                  bgcolor: isDark ? "rgba(255,255,255,0.06)" : "action.hover",
                },
              }}
            />
          ) : (
            <Box aria-hidden sx={circleBase(isDark)} />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}
